import React from 'react';
import bdAuth from '@bd-uikit-web/js-helpers/bdAuth';

import Icon from '@bd-uikit-web/React/Icon';

export default class UploadAreaBox extends React.Component {
  constructor(props) {
    super();

    this.xhr = null;
    this._baseClass = 'upload-area';
    this._allowedTypes = props.allowedTypes || ['*'];
    this._inlineStyles = {};
    this._uploadUrl = 'https://upload.blackdove.io/files';
    this._onStart = props.onStart || null;

    this._onComplete = props.onComplete || null;
    this._maxFileSizeMB = props.maxFileSizeMB || 1000; // 1GB default max file size
    this._onFileChange = props.onFileChange || null;
    this._sublineText = props.subline || null;
    this._onCancel = props.onCancel || null;

    this.state = {
      uploading: false,
      uploadDetails: {
        fileName: null,
        percentComplete: null,
      },
      hasError: false,
      draggingOver: false,
      instructionText: props.instructionText || 'Drag & drop your file or click here to upload',
    };
    this.uploadingCanceled = false,

    this.onClick = this.onClick.bind(this);
    this.cancelUploading = this.cancelUploading.bind(this);
    this.onDragOver = this.onDragOver.bind(this);
    this.onDragEnter = this.onDragEnter.bind(this);
    this.onDragLeave = this.onDragLeave.bind(this);
    this.onFileChange = this.onFileChange.bind(this);
  }

  onFileChange(e) {
    e.preventDefault();

    const files = e.target.files || e.dataTransfer.files;
    const file = files[0];

    if (typeof this._onFileChange === 'function') {
      // if there is an on file change callback
      // wait untill it completes to continue the upload
      this._onFileChange(file, (proceed) => {
        if (proceed) {
          this.checkFileValid(file);
        }
      });
    } else {
      this.checkFileValid(file);
    }

    return;
  }

  onClick() {
    if (!this.state.uploading) {
      this.refs['file-picker'].click();
    }
  }

  onDragOver(e) {
    e.preventDefault();
  }

  onDragEnter(e) {
    e.preventDefault();
    this.setState({
      draggingOver: true,
    });
  }

  onDragLeave() {
    this.setState({
      draggingOver: false,
    });
  }

  getBaseClass() {
    let baseClass = this._baseClass;

    if (this.state.uploading) {
      baseClass += ' upload-area--uploading';
    }

    if (this.state.draggingOver) {
      baseClass += ' upload-area--hover';
    }

    if (this.state.hasError) {
      baseClass += ' upload-area--error';
    }

    return baseClass;
  }

  checkFileType(file) {
    const allowedTypes = this._allowedTypes;

    if (allowedTypes.indexOf(file.type) === -1 &&
      allowedTypes.indexOf('*') === -1) {
      // if filetype is not in allowedTypes
      // and allowedTypes also doesn't contains a wildcard
      // then the filetype is not allowed
      return false;
    }

    return true;
  }

  getSymbolNode() {
    return (
      <div className="upload-area__instruction__logo">
        <img src="/assets/img/bd-logo-dark.svg" alt="Blackdove Logo" />
      </div>
    );
  }

  cancelUploading() {
    this.uploadingCanceled = true;

    // allows same file to upload as inputs's onChange will not fire if same video is uploaded after cancelling
    this.refs['file-picker'].value = '';
    if (this.xhr) {
      this.xhr.abort();
    }
  }

  uploadFile(file) {
    if (this.state.uploading) {
      return;
    }

    // if request successfull, trigger callback
    if (typeof this._onStart === 'function') {
      this._onStart(file);
    }

    if (this.uploadingCanceled) {
      this.uploadingCanceled = false;
      return;
    }

    const formData = new FormData();
    this.xhr = new XMLHttpRequest();
    const url = this._uploadUrl;

    formData.append('file', file);

    this.setState({
      uploadDetails: {
        fileName: file.name,
        percentComplete: 0,
      },
    });

    this.xhr.upload.addEventListener('progress', (e) => {
      if (!e.lengthComputable) {
        return;
      }

      const percentComplete = Math.round((e.loaded / e.total) * 100);
      this.setState({
        uploadDetails: {
          fileName: file.name,
          percentComplete,
        },
      });
    }, false);

    this.xhr.addEventListener('error', () => {
      if (this.uploadingCanceled) {
        this.setState({
          uploading: false,
          hasError: false,
        });
        this.uploadingCanceled = false;
        return;
      }
      this.setState({
        uploading: false,
        hasError: true,
        instructionText: `Looks like something went wrong. Please try again. (${this.xhr.status})`,
      });
      this.xhr.abort();
    }, false);

    this.xhr.addEventListener('readystatechange', () => {
      if (this.xhr.readyState !== this.xhr.DONE) {
        return;
      }

      if (this.xhr.status === 200) {
        // parse response JSON
        const jsonRes = JSON.parse(this.xhr.responseText);

        // if request successfull, trigger callback
        if (typeof this._onComplete === 'function') {
          this._onComplete(jsonRes);
        }
      } else {
        if (this.uploadingCanceled) {
          this.setState({
            uploading: false,
            hasError: false,
          });
          this.uploadingCanceled = false;
          return;
        }
        this.setState({
          uploading: false,
          hasError: true,
          instructionText: `Looks like something went wrong. Please try again. (${this.xhr.status})`,
        });
      }
    }, false);

    this.xhr.open('POST', url, true);
    this.xhr.setRequestHeader('Authorization', bdAuth.getTokenHeader());
    this.xhr.setRequestHeader('cache-control', 'no-cache');

    this.xhr.send(formData);
  }

  checkFileValid(file) {
    const allowedTypes = this._allowedTypes;

    const validType = this.checkFileType(file);
    const fileSizeMB = file.size / 1000000;
    const maxFileSizeMB = this._maxFileSizeMB;

    if (validType) {
      // valid file type
      if (fileSizeMB < maxFileSizeMB) {
        // valid size
        // start uploading
        this.setState({
          draggingOver: false,
          hasError: false,
          uploading: true,
        });

        this.uploadFile(file);
      } else {
        this.setState({
          instructionText: `Whoops that file is too big. The maximum file size is ${maxFileSizeMB}MB. Your file is ${parseFloat(fileSizeMB).toFixed(2)}MB.`,
          draggingOver: false,
          hasError: true,
        });
      }
    } else {
      this.setState({
        instructionText: `Whoops that filetype is not allowed. The allowed types are ${allowedTypes.join(', ')}.`,
        draggingOver: false,
        hasError: true,
      });
    }
  }

  render() {
    return (
      <section
        className={this.getBaseClass()}
        style={this._inlineStyles}
        onClick={this.onClick}
        onDrop={this.onFileChange}
        onDragOver={this.onDragOver}
        onDragEnter={this.onDragEnter}
        onDragLeave={this.onDragLeave}
      >
        <input
          className="upload-area__file-picker"
          onChange={this.onFileChange}
          ref="file-picker" name="file" type="file"
        />
        <div className="upload-area__instruction">
          <div className="upload-area__instruction__wrapper">
            {this.getSymbolNode()}
            <p className="upload-area__instruction__cap">
              {this.state.instructionText}
              {!this.state.hasError && this._sublineText ? <br /> : null}
              {!this.state.hasError && this._sublineText ? this._sublineText : null}
            </p>
          </div>
        </div>
        <div className="upload-area__overlay">
          <div
            className="upload-area__overlay__progress"
            style={{right: `${100 - this.state.uploadDetails.percentComplete}%`}}
          ></div>
          <div className="upload-area__overlay__content">
            <h1 className="upload-area__overlay__percentage">
              {this.state.uploadDetails.percentComplete}%
            </h1>
            {this.state.uploadDetails.fileName}
          </div>
          <div
              className="upload-area__overlay__cancel"
              onClick={(e) => {
                e.stopPropagation();
                this._onCancel();
              }}
            >
            <Icon name="android-close" />
          </div>
        </div>
      </section>
    );
  }
}
